import { useEffect, useState } from 'react';
import { useJobTaskApi } from '../../../clients/quipli-service/job-tasks/job-task-api';
import { JobType, Job, TaskType } from '../../../clients/quipli-service/job-tasks/job-tasks';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { Order } from '../../../clients/quipli-service/order/order';
import { Collapsible } from '../../../components/collapsible/collapsible';
import Icon from '../../../components/icons/icon';
import { Button } from '../../../components/form/button';
import { Product, ProductInventoryUnit } from '../../../clients/quipli-service/product/product';
import { Modal } from '../../../components/modal/modal';
import { InspectionModalContent } from './inspection-modal/inspection-modal';
import { OrderSection } from '../../order/order-section/order-section';
import { toast } from '../../../util/toasts';
import { ProductImageV2 } from '../../../components/image/product/product-image-v2';
import { MediaCard } from '../../order/view/order-media-list';
import { useOrderMedia } from '../../order/hooks/use-order-media';

export const InspectionView = ({
  order,
  jobType,
  unitTrackingVia,
  fuelUnit,
  fetchOrder,
}: {
  order: Order;
  jobType: JobType;
  unitTrackingVia: 'serialNumber' | 'unitNumber';
  fuelUnit: 'gallons' | 'liters';
  fetchOrder: () => void;
}) => {
  const { getJobByEntity, startJob } = useJobTaskApi();
  const [inspection, setInspection] = useState<Job>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showInspectionSummary, setShowInspectionSummary] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState<ProductInventoryUnit>();

  const { isOrderMediaLoading, mediaList, fetch } = useOrderMedia(order);

  const fetchJob = async () => {
    setLoading(true);
    const job = await getJobByEntity('Order', `${order.id}`, jobType);
    setInspection(job);
    setLoading(false);
  };

  useEffect(() => {
    fetchJob();
  }, []);

  const initInspection = async () => {
    try {
      setLoading(true);
      const job = await startJob(order.id, jobType);
      setInspection(job);
      setSelectedUnit(
        order.products
          .find((_) =>
            _.assignedUnits.some(
              (u) => u.id === job.tasks.find((_) => _.metadata?.productUnitId)?.metadata?.productUnitId,
            ),
          )
          ?.assignedUnits.find(
            (u) => u.id === job.tasks.find((_) => _.metadata?.productUnitId)?.metadata?.productUnitId,
          ),
      );
    } catch (e) {
      console.error(e);
      toast('Failed to start inspection', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <OrderSection>
      <div className="inspection-view">
        {loading || isOrderMediaLoading ? (
          <div className="flex justify-between items-center">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              {jobType === JobType.CheckoutInspection ? 'Check out Inspection' : 'Check in Inspection'}
            </Typography>
            <Spinner />
          </div>
        ) : !inspection ? (
          <div className="flex justify-between items-center">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              {jobType === JobType.CheckoutInspection ? 'Check out Inspection' : 'Check in Inspection'}
            </Typography>
            <Button
              disabled={loading || isOrderMediaLoading}
              className="button button-yellow"
              onClick={() => initInspection()}
            >
              Start
            </Button>
          </div>
        ) : inspection && inspection.tasks?.length > 0 ? (
          <div>
            <div
              className="mb-3 inspection-header-collapsible-trigger flex justify-between items-center cursor-pointer"
              onClick={() => setShowInspectionSummary(!showInspectionSummary)}
            >
              <Typography type="text-md" weight="medium" className="text-grey-800">
                {jobType === JobType.CheckoutInspection ? 'Check out Inspection' : 'Check in Inspection'}
              </Typography>
              {inspection.tasks?.length ? (
                <div
                  className={`flex items-center gap-2 ${
                    inspection.status === 'Complete'
                      ? inspection.tasks.find(
                          (t) =>
                            t.type === TaskType.InspectionChecklist &&
                            t.metadata?.checklist.some((item: any) => item.value === false),
                        )
                        ? 'text-red-500'
                        : 'text-green-500'
                      : inspection.status === 'InProgress'
                      ? 'text-yellow-500'
                      : 'text-grey-500'
                  }`}
                >
                  {inspection.status === 'Complete' &&
                    inspection.tasks.find(
                      (t) =>
                        t.type === TaskType.InspectionChecklist &&
                        t.metadata?.checklist.some((item: any) => item.value === false),
                    ) && <Icon icon="CircleExclamation" stroke="yellow" />}
                  <Typography type="text-sm" weight="semi-bold">
                    {inspection.status === 'NotStarted'
                      ? 'Not Started'
                      : inspection.status === 'InProgress'
                      ? 'In Progress'
                      : `Complete${
                          inspection.tasks.find(
                            (t) =>
                              t.type === TaskType.InspectionChecklist &&
                              t.metadata?.checklist.some((item: any) => item.value === false),
                          )
                            ? ' - Not Passed'
                            : ''
                        }`}
                  </Typography>
                  <Icon className="cursor-pointer" icon={showInspectionSummary ? 'ChevronUp' : 'ChevronDown'} />
                </div>
              ) : (
                <Button
                  disabled={loading || isOrderMediaLoading}
                  className="button button-yellow"
                  onClick={() => initInspection()}
                >
                  Start
                </Button>
              )}
            </div>
            {/* begin inspection summary stuff */}
            {showInspectionSummary &&
              order.products.flatMap((orderProduct) =>
                orderProduct.assignedUnits.map((unit) => {
                  const hoursTask = inspection.tasks.find(
                    (task) => task.metadata?.productUnitId === unit.id && task.type === TaskType.MachineHours,
                  );
                  const checklistTask = inspection.tasks.find(
                    (task) => task.metadata?.productUnitId === unit.id && task.type === TaskType.InspectionChecklist,
                  );
                  return (
                    <div key={unit.id} className="inspection-unit bg-grey-50 rounded-md p-2">
                      <Collapsible
                        trigger={() => (
                          <div className="flex justify-between items-center">
                            <InspectionUnitHeader product={orderProduct.product} unitNumber={unit[unitTrackingVia]} />
                            {(!hoursTask || hoursTask.status === 'Complete') &&
                            (!checklistTask || checklistTask.status === 'Complete') &&
                            inspection.tasks.find(
                              (t) => t.metadata?.productUnitId === unit.id && t.type === TaskType.MediaUpload,
                            )?.status === 'Complete' ? (
                              <div className="flex gap-2 items-center">
                                {checklistTask?.metadata?.checklist.some((item: any) => item.value === false) ? (
                                  <Icon icon="Warning" stroke="yellow" size="small" />
                                ) : (
                                  <></>
                                )}
                                <div
                                  className={`flex items-center gap-2 ${
                                    inspection.tasks?.find((_) => _.metadata?.productUnitId === unit.id)?.status ===
                                    'Complete'
                                      ? inspection.tasks.find(
                                          (t) =>
                                            t.metadata?.productUnitId === unit.id &&
                                            t.type === TaskType.InspectionChecklist &&
                                            t.metadata?.checklist.some((item: any) => item.value === false),
                                        )
                                        ? 'text-red-500'
                                        : 'text-green-500'
                                      : inspection.tasks?.find((_) => _.metadata?.productUnitId === unit.id)?.status ===
                                        'InProgress'
                                      ? 'text-yellow-500'
                                      : 'text-grey-500'
                                  }`}
                                >
                                  <Typography type="text-sm" weight="semi-bold">
                                    {inspection.tasks?.find((_) => _.metadata?.productUnitId === unit.id)?.status ===
                                    'NotStarted'
                                      ? 'Not Started'
                                      : inspection.tasks?.find((_) => _.metadata?.productUnitId === unit.id)?.status ===
                                        'InProgress'
                                      ? 'In Progress'
                                      : `Complete${
                                          inspection.tasks.find(
                                            (t) =>
                                              t.type === TaskType.InspectionChecklist &&
                                              t.metadata?.productUnitId === unit.id &&
                                              t.metadata?.checklist.some((item: any) => item.value === false),
                                          )
                                            ? ' - Not Passed'
                                            : ''
                                        }`}
                                  </Typography>
                                </div>
                              </div>
                            ) : (
                              <Button className="button button-yellow" onClick={() => setSelectedUnit(unit)}>
                                {checklistTask?.startedAt ? 'Resume' : 'Start'}
                              </Button>
                            )}
                          </div>
                        )}
                        openAtStart={false}
                      >
                        <div>
                          {hoursTask && (
                            <div className="divider-top">
                              <p className="text-sm font-semibold mb-2">{hoursTask.header}</p>
                              {orderProduct.product.allowHourTracking && (
                                <div className="flex justify-between mb-2">
                                  <Typography type="text-sm">
                                    Machine hours at {jobType === JobType.CheckoutInspection ? 'check out' : 'check in'}
                                  </Typography>
                                  <Typography type="text-sm">
                                    {hoursTask.metadata?.hours ? `${hoursTask.metadata.hours} hours` : 'Pending'}
                                  </Typography>
                                </div>
                              )}
                              {jobType === JobType.CheckinInspection && orderProduct.product.fuelChargeProductId && (
                                <div className="flex justify-between mb-2">
                                  <Typography type="text-sm">Fuel at check in</Typography>
                                  <Typography type="text-sm">
                                    {hoursTask.metadata?.fuel ? `${hoursTask.metadata.fuel} ${fuelUnit}` : 'Pending'}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          )}

                          {checklistTask && (
                            <div className="divider-top">
                              <p className="text-sm font-semibold">{checklistTask.header}</p>
                              {checklistTask.metadata?.checklist.map((item: any) => (
                                <>
                                  <div className="flex items-center justify-between mt-2">
                                    <Typography type="text-sm">{item.text}</Typography>
                                    {item.value === undefined ? (
                                      <p className="text-sm text-grey-500">Pending</p>
                                    ) : item.value === false ? (
                                      <div className="flex gap-1 items-center">
                                        <Icon icon="X" size="small" stroke="red" />
                                        <p className="m-0 text-sm text-red-500">Not Passed</p>
                                      </div>
                                    ) : (
                                      <div className="flex gap-1 items-center">
                                        <Icon icon="Check" stroke="green" />
                                        <p className="m-0 text-sm text-green-500">Passed</p>
                                      </div>
                                    )}
                                  </div>
                                  {item.notes && <p className="text-sm ml-4 my-0 text-grey-500">{item.notes}</p>}
                                  {item.media && item.media.length > 0 && (
                                    <div className="overflow-x-auto flex gap-2 mt-2 pb-4">
                                      {item.media.map((id: any) => {
                                        const media = mediaList.find((media) => media.id === id);
                                        if (!media) return <></>;
                                        return (
                                          <MediaCard key={id} media={media!} reload={fetch} enableControls={true} />
                                        );
                                      })}
                                    </div>
                                  )}
                                </>
                              ))}
                            </div>
                          )}
                        </div>
                      </Collapsible>
                    </div>
                  );
                }),
              )}

            {selectedUnit !== undefined && (
              <Modal
                className="inspection-modal"
                isOpen={selectedUnit !== undefined}
                close={() => setSelectedUnit(undefined)}
              >
                <InspectionModalContent
                  unitTrackingVia={unitTrackingVia}
                  fuelUnit={fuelUnit}
                  unit={selectedUnit!}
                  job={inspection}
                  product={order.products.find((p) => p.assignedUnits.some((u) => u.id === selectedUnit?.id))!.product}
                  close={() => {
                    setSelectedUnit(undefined);
                    fetchJob().then(() => fetchOrder());
                  }}
                  setInspection={setInspection}
                  order={order}
                />
              </Modal>
            )}
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              {jobType === JobType.CheckoutInspection ? 'Check out Inspection' : 'Check in Inspection'}
            </Typography>
            <Typography type="text-sm" className="text-grey-500">
              Not required
            </Typography>
          </div>
        )}
      </div>
    </OrderSection>
  );
};

export const InspectionUnitHeader = ({
  product,
  unitNumber,
}: // progressBar
{
  product: Product;
  unitNumber: string; // either unitNumber or serialNumber
  progressBar?: { current: number; total: number };
}) => {
  return (
    <div className="flex items-center justify-between bg-grey-50 rounded-md p-2 my-3">
      <div className="flex items-center">
        <ProductImageV2 productId={product.id} profile="MINI" />
        <div className="flex flex-col ml-2">
          <Typography type="text-sm" weight="medium">
            {product.name}
          </Typography>
          <Typography type="text-xs" className="text-grey-500">
            Unit {unitNumber}
          </Typography>
        </div>
      </div>
      {/* {progressBar && (
        <CircularProgress
          centerText={`${progressBar.current}/${progressBar.total}`}
          percentage={Math.round((progressBar.current / progressBar.total) * 100)}
          className="w-5 h-5"
        />
      )} */}
    </div>
  );
};
